import {useRef, useCallback, useEffect, type ChangeEvent, type PointerEvent as ReactPointerEvent} from 'react'
import {Box, TextField} from '@mui/material'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── Constants ─────────────────────────────────────────────────────

const PAD_SIZE = 120
const HANDLE_RADIUS = 6
const PHONE_W = 20
const PHONE_H = 36
const RANGE = 400

// ── LightDirectionPad — 2D pad controlling X/Y with inputs ───────

interface LightDirectionPadProps {
  valueX: number
  valueY: number
  onChange: (x: number, y: number) => void
  onDragEnd?: () => void
}

const LightDirectionPad = ({valueX, valueY, onChange, onDragEnd}: LightDirectionPadProps) => {
  const tokens = useAppTokens()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const draggingRef = useRef(false)

  // Map scene value to canvas pixel
  const valueToPx = useCallback((val: number) => ((val + RANGE / 2) / RANGE) * PAD_SIZE, [])

  // Map canvas pixel to scene value
  const pxToValue = useCallback((px: number) => {
    const clamped = Math.max(0, Math.min(PAD_SIZE, px))
    return Math.round((clamped / PAD_SIZE) * RANGE - RANGE / 2)
  }, [])

  const updateFromEvent = useCallback(
    (e: PointerEvent | ReactPointerEvent) => {
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      // Single callback with both axes to avoid race conditions
      onChange(pxToValue(x), pxToValue(PAD_SIZE - y))
    },
    [pxToValue, onChange],
  )

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      draggingRef.current = true
      updateFromEvent(e)
    },
    [updateFromEvent],
  )

  const handleInputX = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value)
      if (!Number.isNaN(val)) onChange(Math.max(-RANGE / 2, Math.min(RANGE / 2, val)), valueY)
    },
    [onChange, valueY],
  )

  const handleInputY = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value)
      if (!Number.isNaN(val)) onChange(valueX, Math.max(-RANGE / 2, Math.min(RANGE / 2, val)))
    },
    [onChange, valueX],
  )

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!draggingRef.current) return
      updateFromEvent(e)
    }
    const handlePointerUp = () => {
      if (draggingRef.current) onDragEnd?.()
      draggingRef.current = false
    }
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [updateFromEvent, onDragEnd])

  // Draw the pad
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = PAD_SIZE * dpr
    canvas.height = PAD_SIZE * dpr
    ctx.scale(dpr, dpr)

    ctx.clearRect(0, 0, PAD_SIZE, PAD_SIZE)

    // Background
    ctx.fillStyle = tokens.sidebar.background
    ctx.beginPath()
    ctx.roundRect(0, 0, PAD_SIZE, PAD_SIZE, 6)
    ctx.fill()

    // Border
    ctx.strokeStyle = tokens.accordion.border
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.roundRect(0.5, 0.5, PAD_SIZE - 1, PAD_SIZE - 1, 6)
    ctx.stroke()

    // Axis lines
    const cx = PAD_SIZE / 2
    const cy = PAD_SIZE / 2
    ctx.strokeStyle = tokens.accordion.border
    ctx.lineWidth = 1
    ctx.setLineDash([3, 3])
    ctx.beginPath()
    ctx.moveTo(cx, 0)
    ctx.lineTo(cx, PAD_SIZE)
    ctx.moveTo(0, cy)
    ctx.lineTo(PAD_SIZE, cy)
    ctx.stroke()
    ctx.setLineDash([])

    // Axis labels
    ctx.fillStyle = tokens.text.muted
    ctx.font = '9px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('X', PAD_SIZE - 8, cy - 4)
    ctx.fillText('Y', cx + 8, 10)

    // Phone icon at center
    const px = cx - PHONE_W / 2
    const py = cy - PHONE_H / 2
    ctx.strokeStyle = tokens.text.muted
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.roundRect(px, py, PHONE_W, PHONE_H, 3)
    ctx.stroke()
    // Screen area
    ctx.fillStyle = tokens.text.muted
    ctx.globalAlpha = 0.15
    ctx.beginPath()
    ctx.roundRect(px + 2, py + 4, PHONE_W - 4, PHONE_H - 8, 1)
    ctx.fill()
    ctx.globalAlpha = 1.0

    // Light handle position (canvas Y inverted for scene Y)
    const hx = valueToPx(valueX)
    const hy = PAD_SIZE - valueToPx(valueY)

    // Line from center to handle
    ctx.strokeStyle = 'rgba(255, 200, 50, 0.4)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(cx, cy)
    ctx.lineTo(hx, hy)
    ctx.stroke()

    // Handle glow
    ctx.beginPath()
    ctx.arc(hx, hy, HANDLE_RADIUS + 4, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(255, 200, 50, 0.15)'
    ctx.fill()

    // Handle
    ctx.beginPath()
    ctx.arc(hx, hy, HANDLE_RADIUS, 0, Math.PI * 2)
    ctx.fillStyle = '#ffcc33'
    ctx.fill()

    // Bulb icon — small rays
    ctx.strokeStyle = 'rgba(255, 200, 50, 0.6)'
    ctx.lineWidth = 1
    const rayLen = 4
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6
      ctx.beginPath()
      ctx.moveTo(hx + Math.cos(angle) * (HANDLE_RADIUS + 2), hy + Math.sin(angle) * (HANDLE_RADIUS + 2))
      ctx.lineTo(
        hx + Math.cos(angle) * (HANDLE_RADIUS + 2 + rayLen),
        hy + Math.sin(angle) * (HANDLE_RADIUS + 2 + rayLen),
      )
      ctx.stroke()
    }
  }, [valueX, valueY, tokens, valueToPx])

  const sx = buildStyles(tokens)

  return (
    <Box sx={sx.wrapper}>
      <canvas
        ref={canvasRef}
        style={{width: PAD_SIZE, height: PAD_SIZE, cursor: 'pointer', touchAction: 'none'}}
        onPointerDown={handlePointerDown}
      />
      <Box sx={sx.inputs}>
        <Box sx={sx.inputRow}>
          <Box component="span" sx={sx.inputLabel}>
            X
          </Box>
          <TextField
            value={valueX}
            onChange={handleInputX}
            onBlur={onDragEnd}
            size="small"
            type="number"
            sx={sx.numberInput}
          />
        </Box>
        <Box sx={sx.inputRow}>
          <Box component="span" sx={sx.inputLabel}>
            Y
          </Box>
          <TextField
            value={valueY}
            onChange={handleInputY}
            onBlur={onDragEnd}
            size="small"
            type="number"
            sx={sx.numberInput}
          />
        </Box>
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  inputs: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  inputLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.muted,
    minWidth: 12,
  },
  numberInput: {
    width: 64,
    '& .MuiOutlinedInput-root': {
      fontSize: 12,
      color: tokens.text.primary,
    },
    '& .MuiOutlinedInput-input': {
      padding: '4px 6px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
  },
})

export default LightDirectionPad
